import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Brain, TrendingUp, ShieldAlert, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrency } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const RISK_META = {
  baixo: { color: "hsl(var(--success))" },
  medio: { color: "hsl(var(--warning))" },
  alto: { color: "hsl(var(--destructive))" },
};

export default function FinanceAI() {
  const { t, lang } = useI18n();

  const { data: forecast } = useQuery({
    queryKey: ["finance-forecast"],
    queryFn: async () => (await api.get("/finance/forecast")).data,
  });
  const { data: risk } = useQuery({
    queryKey: ["finance-risk"],
    queryFn: async () => (await api.get("/finance/risk")).data,
  });
  const { data: insights } = useQuery({
    queryKey: ["finance-insights", lang],
    queryFn: async () => (await api.get("/finance/insights", { params: { lang } })).data,
  });

  return (
    <div className="space-y-6" data-testid="finance-ai-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-sm bg-secondary flex items-center justify-center">
          <Brain className="h-5 w-5 text-[hsl(var(--success))]" />
        </div>
        <div>
          <h1 className="font-display text-4xl flex items-center gap-3">
            {t("finance_title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("finance_sub")}</p>
        </div>
      </div>

      <Card className="rounded-sm border-border overflow-hidden" data-testid="finance-forecast">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm uppercase tracking-wider text-muted-foreground">{t("forecast_section")}</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
          {(forecast?.months || []).map((m, i) => (
            <motion.div key={m.month} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }} className="p-6" data-testid={`forecast-month-${i}`}>
              <p className="text-xs uppercase text-muted-foreground">{m.month}</p>
              <p className="font-display text-2xl tabular-nums mt-1">{fmtCurrency(m.projected_net)}</p>
              <p className="text-xs text-muted-foreground mt-1">{t("projected_net")}</p>
            </motion.div>
          ))}
        </div>
      </Card>

      <Card className="rounded-sm border-border overflow-hidden" data-testid="finance-risk">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm uppercase tracking-wider text-muted-foreground">{t("risk_section")}</h3>
        </div>
        {(risk?.contracts || []).length === 0 ? (
          <p className="text-sm text-muted-foreground p-6">{t("no_active_contracts")}</p>
        ) : (
          <div className="divide-y divide-border">
            {risk.contracts.map((c) => {
              const meta = RISK_META[c.risk_level] || RISK_META.baixo;
              return (
                <div key={c.contract_id} className="flex items-center justify-between px-6 py-4" data-testid={`risk-${c.contract_id}`}>
                  <div>
                    <p className="text-sm font-medium">{c.property_name}</p>
                    <p className="text-xs text-muted-foreground">{c.months_evaluated} {t("months_evaluated")} · {(c.on_time_ratio * 100).toFixed(0)}%</p>
                  </div>
                  <Badge variant="outline" className="rounded-sm" style={{ color: meta.color, borderColor: meta.color }}>
                    {t(`risk_${c.risk_level === "medio" ? "medium" : c.risk_level}`)}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="p-6 rounded-sm border-border" data-testid="finance-insights">
        <div className="flex items-center gap-2 mb-4 text-[hsl(var(--success))]">
          <Sparkles className="h-4 w-4" /><span className="text-xs uppercase tracking-wider">{t("insights_section")}</span>
        </div>
        <ul className="space-y-2">
          {(insights?.insights || []).map((note, i) => (
            <li key={i} className="text-sm leading-relaxed" data-testid={`insight-${i}`}>• {note}</li>
          ))}
        </ul>
        {insights && insights.ai_generated === false && (
          <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border" data-testid="finance-ai-fallback-note">
            {t("ai_fallback_note")}
          </p>
        )}
      </Card>
    </div>
  );
}
